import { FC, useCallback } from "react";
import { useSelector } from "react-redux";
import { EmptyMessage } from "src/components";
import { PdTableCellText } from "src/components/layout/table";
import { useFocusableQuery } from "src/hooks/api";
import { tColumn } from "src/hooks/table";
import api from "src/util/apiNew";
import { formatMinMaxMoney } from "src/util/gFormatting";
import { tProductReport } from "tmt_api/src/types/dtos";

import { PdSpinner, ReportTable } from "./components";
import { formatDate, getParams } from "./utils";

const getNewProductsReport = (start: Date, end: Date) =>
  api.get<tProductReport[]>("reports/new-products", getParams(start, end)).then((res) => res.data);

const columns: tColumn<tProductReport>[] = [
  {
    id: "dateCreatedInSeconds",
    header: "Date",
    accessor: (originalRow) => formatDate(originalRow.dateCreatedInSeconds),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 120 },
  },
  {
    id: "brandName",
    header: "Brand Name",
    accessor: (originalRow) => originalRow.brandName,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "name",
    header: "Name",
    accessor: (originalRow) => originalRow.name,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 260 },
  },
  {
    id: "maxWholesalePrice",
    header: "Wholesale Price",
    accessor: (originalRow) => formatMinMaxMoney(originalRow.minWholesalePrice, originalRow.maxWholesalePrice),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 160 },
  },
  {
    id: "maxMsrpPrice",
    header: "Retail Price",
    accessor: (originalRow) => formatMinMaxMoney(originalRow.minMsrpPrice, originalRow.maxMsrpPrice),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 160 },
  },
  {
    id: "isPublished",
    header: "Status",
    accessor: (originalRow) => (originalRow.isPublished ? "Published" : "Unpublished"),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 160 },
  },
];

const NewProductsTable: FC<{ period: Date[] }> = ({ period }) => {
  const config = useSelector((state) => state.TableConfigReducer.newProductsExcelFormat);
  const { isLoading, data } = useFocusableQuery<tProductReport[]>(
    useCallback(() => getNewProductsReport(period[0], period[1]), [period]),
    { enabled: !!(period[0] && period[1]) },
  );

  if (isLoading) {
    return <PdSpinner />;
  }

  if (!data || data?.length === 0) {
    return <EmptyMessage message="There is no data to display" />;
  }

  return (
    <ReportTable
      data={data}
      columns={columns}
      keyExtractor={(item) => `${item.sku}-${item.name}`}
      sortedBy="dateCreatedInSeconds"
      columnsConfig={config}
      tableConfigReduxKey="newProductsExcelFormat"
    />
  );
};

export default NewProductsTable;
